import gettext from '@sdk/lang/gettext';
import { isFeatureEnabled } from '@sdk/utils/isFeatureEnabled';

import config from '@angular/config';
import { TIMECLOCK_GEOFENCING_MODES, MODAL_CONFIRMATION_STATE_DELAY } from '@angular/constants';

class ClockInCtrl {
  TIME_CARD_NOTES_ENABLED = config.TIME_CARD_NOTES_ENABLED;
  PLACEHOLDER_TEXTS = {
    position: gettext('Add position'),
    location: gettext('Add location'),
    notes: gettext('Add notes'),
  };

  constructor(
    Authentication,
    AlertService,
    EventCollection,
    PersonaCollection,
    Restangular,
    ShiftUtils,
    Utils,
    toTimeFilter,
    extendShiftModel,
    sdk,
    TimesheetsUtils,
    $window,
    $q,
    $http,
    $timeout,
    $rootScope,
    $uibModalInstance,
    $uibModal,
    /* resolves */ shifts,
    readOnly,
    lastLocationAndPosition,
    upcomingShifts,
    TimesheetsService
  ) {
    'ngInject';

    Object.assign(this, {
      Authentication,
      AlertService,
      EventCollection,
      PersonaCollection,
      Restangular,
      ShiftUtils,
      Utils,
      toTimeFilter,
      extendShiftModel,
      sdk,
      TimesheetsUtils,
      $window,
      $q,
      $http,
      $timeout,
      $rootScope,
      $uibModalInstance,
      $uibModal,
      shifts,
      readOnly,
      lastLocationAndPosition,
      upcomingShifts,
      TimesheetsService,
    });
    this.TIMESHEET_ENTRY_TYPES = sdk('constants').TIMESHEET_ENTRY_TYPES;
  }

  $onInit() {
    this.coworkersByShiftId = {};
    this.shift = this.shifts[0];

    this.addNoteMode = false;
    this.noteValueHasChanged = false;

    this.clockRadiusInMeters = this.TimesheetsService.getTimeclockRadiusInMeters();

    this.AUTOMATED_BREAKS_ENABLED = isFeatureEnabled(
      config.AUTOMATED_BREAKS_ENABLED,
      this.Authentication.user.org.id
    );

    if (this.shift) {
      this.shiftChanged();
    } else {
      this.nonDeletableGroupIds = [];
      ['position', 'location'].forEach((group) => {
        this[`${group}s`] = this.PersonaCollection.me[`${group}s`];
        if (this.lastLocationAndPosition[group]) {
          this[group] = this[`${group}s`].find(
            (g) => g.id === this.lastLocationAndPosition[group].id
          );
        }

        if (!this[group] && this[`${group}s`].length === 1) {
          this[group] = this[`${group}s`][0];
        }

        if (this[`${group}s`].length === 1 && this[group]) {
          this.nonDeletableGroupIds.push(this[group].id);
        }
      });
    }
  }

  selectNextShift() {
    this.shift = this.shifts[this.shiftIndex + 1];
    this.shiftChanged();
  }

  selectPreviousShift() {
    this.shift = this.shifts[this.shiftIndex - 1];
    this.shiftChanged();
  }

  shiftChanged() {
    if (!this.shift) {
      return;
    }

    this.shiftIndex = this.shifts.indexOf(this.shift);
    this.EventCollection.getEvents(this.shift.dtstart, this.shift.dtstart); // to fetch notes
    this.startTime = this.toTimeFilter(this.shift.dtstart);
    this.endTime = this.toTimeFilter(this.shift.dtend);

    this._getCoworkers();
  }

  async _getCoworkers() {
    if (!this.shift) {
      return;
    }

    if (!(this.shift.id in this.coworkersByShiftId)) {
      const coworkers = await this.EventCollection.getShiftCoworkers(this.shift.id);
      this.coworkersByShiftId[this.shift.id] = this.Restangular.restangularizeCollection(
        null,
        coworkers,
        'users'
      );
    }

    this.coworkers = this.coworkersByShiftId[this.shift.id];
  }

  get dayNotes() {
    if (this.shift) {
      return this.EventCollection.notesDict[this.Utils.formatDate(this.shift.dtstart)];
    }

    return null;
  }

  async _goToConfirmationState(title, subtitle, shift) {
    this.confirmationTitle = title;
    this.confirmationSubtitle = subtitle;
    this.confirmationState = true;
    this.confirmationAfterShift = shift;

    if (this.confirmationAfterShift) {
      this.shift = this.confirmationAfterShift;
      this.shiftChanged();
    }

    await this.$timeout(MODAL_CONFIRMATION_STATE_DELAY);

    this.confirmationClicked();
  }

  confirmationClicked() {
    if (!this.confirmationAfterShift) {
      return this.$uibModalInstance.close();
    }

    this.shift = this.confirmationAfterShift;
    this.confirmationState = false;
  }

  async clockIn() {
    if (!this.shift) {
      this.shiftlessForm?.$setSubmitted();

      if (!this.position || !this.location) {
        throw 'No position or location selected';
      }
    }

    const { orgSettings } = this.Authentication.user;
    let coords = {};

    if (
      orgSettings.clockInGeoRequired &&
      orgSettings.clockInGeoMode !== TIMECLOCK_GEOFENCING_MODES.ONLY_CLOCK_OUT &&
      orgSettings.clockInMaxDistance
    ) {
      coords = await this.Utils.getCoordinates();
    }

    try {
      const { longitude, latitude } = coords;
      const params = this.shift
        ? {
            shiftId: this.shift.id,
          }
        : {
            locationId: this.location.id,
            positionId: this.position.id,
          };
      const shift = await this.EventCollection.clockIn({ longitude, latitude }, false, params);
      await this.addTimeclockNote();
      const time = this.toTimeFilter(
        this.Utils.getRealDate(
          shift.timesheetProjections[shift.timesheetProjections.length - 1].lastAction
        )
      );

      if (config.REDESIGNED_CLOCK_IN_MODAL_ENABLED) {
        this.$uibModalInstance.close({ openConfirmationModal: true });
      } else {
        this._goToConfirmationState('Clocked in', time);
      }
    } catch (error) {
      this._rectifyShiftInCache(error);
      throw error;
    }
  }

  async clockOut() {
    const { orgSettings } = this.Authentication.user;
    let coords = {};

    if (
      orgSettings.clockInGeoRequired &&
      orgSettings.clockInGeoMode !== TIMECLOCK_GEOFENCING_MODES.ONLY_CLOCK_IN &&
      orgSettings.clockInMaxDistance
    ) {
      coords = await this.Utils.getCoordinates();
    }

    try {
      const { longitude, latitude } = coords;
      await this.handleAddNoteEventDuringClockOut(this.shift);

      let shift = await this.EventCollection.clockOut({ longitude, latitude });
      shift = this.extendShiftModel(shift);
      const time = this.toTimeFilter(
        shift.timesheetProjections[shift.timesheetProjections.length - 1].lastAction
      );
      // When automated breaks are on we do not show this confirmation state
      this._goToConfirmationState(
        'Clocked out',
        time,
        this.AUTOMATED_BREAKS_ENABLED ? null : shift
      );
    } catch (error) {
      this._rectifyShiftInCache(error);
      throw error;
    }
  }

  async handleAddNoteEventDuringClockOut(shift) {
    try {
      const data = await this.addTimeclockNote();
      if (data?.assigneeNotes || data?.assigneeNotes === '') {
        shift.assigneeNotes = data.assigneeNotes;
      }
    } catch (error) {
      this.AlertService.danger('Your note has not been added.');
    }
  }

  _rectifyShiftInCache(error) {
    if (error.status === 406 && error.data.shift) {
      this.EventCollection.addOrEditEvents([error.data.shift]);
    }
  }

  get now() {
    return this.Utils.getNowDate();
  }

  getBreakMinutes(timesheetProjection) {
    let minutes = timesheetProjection.breakMinutes;

    if (timesheetProjection.onBreak) {
      minutes += (this.now - timesheetProjection.lastActionLocal) / 1000 / 60;
    }

    return Math.floor(minutes);
  }

  get totalDuration() {
    return this.shift.timesheetProjections.reduce((prev, projection) => {
      if (projection.clockOut) {
        return (
          prev +
          Math.floor(this.Utils.subtractDates(projection.clockOut, projection.clockIn) / 1000 / 60)
        );
      }

      return prev;
    }, 0);
  }

  getClockedInMinutes(projection) {
    const end = projection.clockOutLocal || this.now;
    return Math.floor(this.Utils.subtractDates(end, projection.clockInLocal) / 1000 / 60);
  }

  canEditTimesheet() {
    return this.shift && this.TimesheetsUtils.canEditTimesheet(this.shift);
  }

  onTaskPreview(previewModalPromise) {
    this.taskPreviewActive = true;
    previewModalPromise.finally(() => {
      this.taskPreviewActive = false;
    });
  }

  async addTimeclockNote() {
    if (!this.TIME_CARD_NOTES_ENABLED || !this.shift || !this.noteValueHasChanged) {
      this.addNoteMode = false;

      return;
    }

    const data = await this.TimesheetsService.addTimeclockNote(this.shift.id, {
      notes: this.shift.assigneeNotes,
    });
    this.addNoteMode = false;

    return data;
  }

  get clockInButtonVisible() {
    if (
      this.shift &&
      !this.shift.clockInStatus.clockedIn &&
      !(this.confirmationAfterShift || this.readOnly) &&
      this.Authentication.user.orgSettings.clockInFromWebAndApps
    ) {
      return true;
    }

    if (!this.shift && this.locations.length && this.positions.length) {
      return true;
    }

    return false;
  }

  get endBreakButtonVisible() {
    if (this.shift && this.shift.clockInStatus.onBreak) {
      return true;
    }
    return false;
  }

  get clockOutButtonVisible() {
    return (
      this.shift &&
      this.shift.clockInStatus.clockedIn &&
      !(this.confirmationAfterShift || this.readOnly)
    );
  }

  get noteVisible() {
    if (!this.TIME_CARD_NOTES_ENABLED) {
      return false;
    }

    if (this.readOnly) {
      return this.shift?.assigneeNotes;
    }

    if (this.confirmationAfterShift) {
      return !(this.canEditTimesheet() && !this.addNoteMode && !this.shift?.assigneeNotes);
    }

    if (this.shift && !this.readOnly) {
      return true;
    }

    return this.shift?.assigneeNotes;
  }

  get addNoteButtonVisible() {
    return this.canEditTimesheet() && this.confirmationAfterShift && !this.shift?.assigneeNotes;
  }

  async saveNote() {
    const res = await this.addTimeclockNote();

    if (res) {
      this.AlertService.success('Your note has been saved successfully.');
    }

    if (this.confirmationAfterShift) {
      this.$uibModalInstance.close();
    }
    return res;
  }

  setAddNoteMode() {
    this.addNoteMode = true;
  }
}

export default ClockInCtrl;
